import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import type { DeadlineUrgency } from '../services/MatchProgressionService';

interface MatchCountdownProps {
  deadline: string;
  urgency: DeadlineUrgency;
  userSubmitted: boolean;
  opponentName?: string;
  roundNumber: number;
  compact?: boolean;
}

function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return 'Expired';

  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const MatchCountdown: React.FC<MatchCountdownProps> = ({
  deadline,
  urgency,
  userSubmitted,
  opponentName,
  roundNumber,
  compact = false,
}) => {
  const [remainingMs, setRemainingMs] = useState(() => {
    return Math.max(0, new Date(deadline).getTime() - Date.now());
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const ms = Math.max(0, new Date(deadline).getTime() - Date.now());
      setRemainingMs(ms);
    }, 30000);

    return () => clearInterval(interval);
  }, [deadline]);

  if (urgency === 'none') return null;

  const deadlineDate = new Date(deadline);
  const formattedDeadline = deadlineDate.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (compact) {
    const colorClass =
      urgency === 'critical'
        ? 'text-red-400'
        : urgency === 'warning'
        ? 'text-amber-400'
        : urgency === 'expired'
        ? 'text-red-500'
        : 'text-white/70';

    return (
      <span className={`text-xs ${colorClass} flex items-center gap-1`}>
        <Clock size={12} />
        {remainingMs > 0 ? formatTimeRemaining(remainingMs) : 'Expired'}
      </span>
    );
  }

  if (userSubmitted) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-start gap-3">
        <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-green-400">
            Round {roundNumber} -- Submitted
          </div>
          <div className="text-sm text-white/70 mt-1">
            Waiting for {opponentName || 'opponent'} to submit.
            Deadline: {formattedDeadline}
            {remainingMs > 0 && (
              <span className="text-white/50 ml-1">
                ({formatTimeRemaining(remainingMs)} remaining)
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  const bgClass =
    urgency === 'critical'
      ? 'bg-red-500/10 border-red-500/30'
      : urgency === 'warning'
      ? 'bg-amber-500/10 border-amber-500/30'
      : urgency === 'expired'
      ? 'bg-red-500/15 border-red-500/40'
      : 'bg-blue-500/10 border-blue-500/30';

  const iconColor =
    urgency === 'critical' || urgency === 'expired'
      ? 'text-red-400'
      : urgency === 'warning'
      ? 'text-amber-400'
      : 'text-blue-400';

  const titleColor =
    urgency === 'critical' || urgency === 'expired'
      ? 'text-red-400'
      : urgency === 'warning'
      ? 'text-amber-400'
      : 'text-blue-400';

  const Icon = urgency === 'critical' || urgency === 'expired' ? AlertTriangle : Clock;

  return (
    <div className={`border rounded-lg p-4 flex items-start gap-3 ${bgClass}`}>
      <Icon size={20} className={`${iconColor} flex-shrink-0 mt-0.5`} />
      <div className="flex-1">
        <div className={`font-semibold ${titleColor}`}>
          Round {roundNumber} -- {remainingMs > 0 ? formatTimeRemaining(remainingMs) + ' remaining' : 'Deadline passed'}
        </div>
        <div className="text-sm text-white/70 mt-1">
          {remainingMs > 0
            ? `Submit your recording by ${formattedDeadline} or you will be automatically eliminated.`
            : 'The submission window has closed. Results will be processed shortly.'}
        </div>
      </div>
    </div>
  );
};

export default MatchCountdown;
